import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Globe } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import { PageSpeakable } from './SEOSchemas';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_LABELS = { us: 'US', uk: 'UK', eu: 'EU', jp: 'Japan', kr: 'Korea', au: 'Australia' };
const REGION_FULL = { us: 'United States', uk: 'United Kingdom', eu: 'European Union', jp: 'Japan', kr: 'South Korea', au: 'Australia' };

export default function ConversionPage() {
  const { pair, detail } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pair) return;
    const parts = pair.split('-to-');
    if (parts.length !== 2) return;
    const [from, to] = parts;
    let gender = null, type = null;
    if (detail) {
      const dp = detail.split('-');
      if (dp.length >= 2) { gender = dp[0]; type = dp.slice(1).join('-'); }
    }
    const params = new URLSearchParams();
    if (type) params.set('clothing_type', type);
    if (gender) params.set('gender', gender);
    const url = `${API}/convert/${from}/${to}${params.toString() ? '?' + params.toString() : ''}`;

    setLoading(true);
    axios.get(url).then(r => {
      setData(r.data);
      const fromName = REGION_FULL[from] || from.toUpperCase();
      const toName = REGION_FULL[to] || to.toUpperCase();
      const typeLabel = detail ? detail.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'Clothing';
      const title = `${fromName} to ${toName} ${typeLabel} Size Conversion`;
      const desc = `Convert ${typeLabel.toLowerCase()} sizes from ${fromName} to ${toName}. Complete size chart with measurements. Free converter.`;
      setCanonical(detail ? `/convert/${pair}/${detail}` : `/convert/${pair}`);
      setMeta(title, desc);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pair, detail]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading...</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold mb-4">Conversion Not Found</h1><Link to="/brands" className="text-[#C5A028]">Browse brands</Link></div>;

  const fromLabel = REGION_FULL[data.from] || data.from_name;
  const toLabel = REGION_FULL[data.to] || data.to_name;
  const typeLabel = detail ? detail.replace(/-/g, ' ').replace(/\bmens\b/i, "Men's").replace(/\bwomens\b/i, "Women's").replace(/\btops\b/i, 'Tops').replace(/\bshoes\b/i, 'Shoes').replace(/\bpants\b/i, 'Pants') : 'All Clothing';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <PageSpeakable name={`${fromLabel} to ${toLabel} Size Conversion`} />
      <Link to="/brands" className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#C5A028] mb-6 transition-colors"><ArrowLeft className="w-4 h-4" /> All Brands</Link>

      <div className="mb-8">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Size Conversion</p>
        <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-5xl font-light tracking-tighter text-[#1C1917] mb-4 speakable-intro">
          {fromLabel} <ArrowRight className="inline w-6 h-6 text-[#C5A028]" /> {toLabel}
        </h1>
        <p className="text-[#78716C] text-lg">{typeLabel} Size Conversion Chart</p>
        <p className="text-sm text-[#78716C] mt-2">Convert your {typeLabel.toLowerCase()} sizes from {fromLabel} ({data.from?.toUpperCase()}) to {toLabel} ({data.to?.toUpperCase()}) sizing instantly.</p>
      </div>

      {data.tables?.map((table, idx) => (
        <div key={idx} className="mb-8">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] mb-4">{table.name}</h2>
          <div className="overflow-x-auto border border-[#E8E2D9]">
            <table className="w-full text-sm" data-testid={`conversion-table-${idx}`}>
              <thead>
                <tr className="bg-[#FAF8F4]">
                  {table.sizes?.[0] && Object.keys(table.sizes[0]).filter(k => ['label', 'us', 'uk', 'eu', 'jp', 'kr', 'au', 'cm', 'chest_cm', 'bust_cm', 'waist_cm', 'hip_cm'].includes(k)).map(k => (
                    <th key={k} className={`text-left p-3 font-semibold border-b border-[#E8E2D9] ${(k === data.from || k === data.to) ? 'text-[#C5A028] bg-[#FAF8F4]' : 'text-[#1C1917]'}`}>
                      {REGION_LABELS[k] || k.replace(/_/g, ' ').replace(/\bcm\b/, '(cm)').replace(/^\w/, c => c.toUpperCase())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.sizes?.map((s, i) => (
                  <tr key={i} className="hover:bg-[#FAF8F4] transition-colors">
                    {Object.entries(s).filter(([k]) => ['label', 'us', 'uk', 'eu', 'jp', 'kr', 'au', 'cm', 'chest_cm', 'bust_cm', 'waist_cm', 'hip_cm'].includes(k)).map(([k, v]) => (
                      <td key={k} className={`p-3 border-b border-[#E8E2D9] ${(k === data.from || k === data.to) ? 'font-bold text-[#1C1917] bg-[#FEFCF5]' : 'text-[#78716C]'}`}>{v || '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Related conversions */}
      <div className="mt-10 bg-[#FAF8F4] border border-[#E8E2D9] p-6">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mb-4">Related Size Conversions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {!detail && ['mens-tops', 'womens-tops', 'mens-shoes', 'womens-shoes', 'mens-pants'].map(d => (
            <Link key={d} to={`/convert/${pair}/${d}`} className="text-sm text-[#C5A028] hover:underline p-2 bg-white border border-[#E8E2D9]">
              {d.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())}
            </Link>
          ))}
          {detail && Object.keys(REGION_FULL).filter(r => r !== data.from && r !== data.to).slice(0, 4).map(r => (
            <Link key={r} to={`/convert/${data.from}-to-${r}/${detail}`} className="text-sm text-[#C5A028] hover:underline p-2 bg-white border border-[#E8E2D9]">
              {REGION_FULL[data.from]} to {REGION_FULL[r]}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/find-my-size" className="text-sm text-[#C5A028] hover:underline">Find My Size</Link>
        <Link to="/compare" className="text-sm text-[#C5A028] hover:underline">Compare Brands</Link>
        <Link to="/brands" className="text-sm text-[#C5A028] hover:underline">All Brands</Link>
      </div>

      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />

      <div className="mt-6 text-xs text-[#78716C]"><strong>Disclaimer:</strong> Sizes are approximate. Always verify with the retailer before purchasing.</div>
    </div>
  );
}
